import React, { useState, useRef, useEffect, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { useNavigate, useLocation } from "react-router-dom";
import SelectList from "../components/SelectList";
import Webcam from "react-webcam";
import { Toaster, toast } from "sonner";
import { FaCamera } from "react-icons/fa";
import { API_BASE } from "../utils/Instance";
import { useAuth } from "../context/AuthContext";
import { getAllBodyParts } from "../utils/bodyPartNormalizer";

const bodyParts = getAllBodyParts().map(part => ({ value: part, label: part }));

export default function PhotoCapture() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth(); // ✅ get token from context

  const [bodyPart, selectBodyPart] = useState(bodyParts[0]);
  const [isFrontCamera, setIsFrontCamera] = useState(true);
  const [image, setImage] = useState(null);
  const [openCamera, setOpenCamera] = useState(false);
  const [timer, setTimer] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [showTimerOptions, setShowTimerOptions] = useState(false);
  const [deviceCapabilities, setDeviceCapabilities] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const webcamRef = useRef(null);
  const timerRef = useRef(null);

  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type");

  // Detect device capabilities for optimal camera settings
  useEffect(() => {
    const detectDeviceCapabilities = async () => {
      try {
        // Check for camera capabilities
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        
        // Check screen resolution for quality hints
        const screenWidth = window.screen.width;
        const screenHeight = window.screen.height;
        const pixelRatio = window.devicePixelRatio || 1;
        
        // Determine optimal settings based on device capabilities
        const capabilities = {
          hasMultipleCameras: videoDevices.length > 1,
          isHighResScreen: screenWidth >= 1920 || pixelRatio >= 2,
          isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
          maxWidth: screenWidth >= 1920 ? 1920 : screenWidth >= 1280 ? 1280 : 720,
          maxHeight: screenHeight >= 1080 ? 1080 : screenHeight >= 720 ? 720 : 480
        };
        
        setDeviceCapabilities(capabilities);
        console.log('Device capabilities detected:', capabilities);
      } catch (error) {
        console.warn('Could not detect device capabilities:', error);
        // Fallback to conservative settings
        setDeviceCapabilities({
          hasMultipleCameras: false,
          isHighResScreen: false,
          isMobile: true,
          maxWidth: 1280,
          maxHeight: 720
        });
      }
    };

    detectDeviceCapabilities();
  }, []);

  // Enhanced photo capture logic with adaptive quality - defined early for event handlers
  const capturePhoto = useCallback(() => {
    try {
      let captureOptions = {
        screenshotFormat: 'image/jpeg',
        screenshotQuality: 1.0 // Maximum quality (0.0 to 1.0)
      };

      // Use device-specific dimensions for optimal quality
      if (deviceCapabilities) {
        captureOptions.width = deviceCapabilities.maxWidth;
        captureOptions.height = deviceCapabilities.maxHeight;
      } else {
        // Fallback to high-quality defaults
        captureOptions.width = 1920;
        captureOptions.height = 1080;
      }

      // Get screenshot with optimal quality settings
      const imageSrc = webcamRef.current?.getScreenshot(captureOptions);
      
      if (imageSrc) {
        setImage(imageSrc);
        const qualityLabel = deviceCapabilities?.isHighResScreen ? "HD" : "High Quality";
        toast.success(`${qualityLabel} Image captured successfully.`);
      }
    } catch (error) {
      console.warn('High quality capture failed, trying standard quality:', error);
      // Fallback to standard quality if high quality fails
      try {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (imageSrc) {
          setImage(imageSrc);
          toast.success("Image captured successfully.");
        }
      } catch (fallbackError) {
        console.error('Capture failed:', fallbackError);
        toast.error("Failed to capture image. Please try again.");
      }
    }
  }, [deviceCapabilities]);

// ✅ Volume button and keyboard event handlers
useEffect(() => {
  const triggerCapture = (event, eventName) => {
    console.log('🔘 Event detected:', eventName, 'keyCode:', event?.keyCode, 'key:', event?.key);
    
    if (openCamera && !image && !timerActive) {
      // Prevent default behavior for all event types
      if (event) {
        event.preventDefault?.();
        event.stopPropagation?.();
        if (event.cancelable !== false) {
          try {
            event.returnValue = false;
          } catch (e) {
            // Ignore errors in older browsers
          }
        }
      }
      console.log(`📸 Capture triggered by ${eventName}`);
      capturePhoto();
    } else {
      console.log('⚠️ Capture blocked - openCamera:', openCamera, 'image:', !!image, 'timerActive:', timerActive);
    }
  };

  // --- Keyboard / Volume key handling ---
  const handleKeyDown = (event) => {
    const triggerKeys = [" ", "Enter", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
    // Android volume buttons: 24 (Volume Up), 25 (Volume Down)
    // Desktop volume simulation: 38 (Arrow Up), 40 (Arrow Down)
    const volumeKeyCodes = [24, 25, 38, 40];

    if (
      triggerKeys.includes(event.key) ||
      volumeKeyCodes.includes(event.keyCode) ||
      (event.keyCode >= 112 && event.keyCode <= 123) // F1-F12 keys
    ) {
      triggerCapture(event, `Keyboard/Volume (${event.key || event.keyCode})`);
    }
  };

  // --- Bluetooth / media buttons ---
  const handleMediaKey = (event) => {
    const triggerMediaKeys = [
      "VolumeUp", "VolumeDown", "MediaPlayPause", "MediaStop",
      "MediaTrackNext", "MediaTrackPrevious",
    ];
    if (triggerMediaKeys.includes(event.key)) {
      triggerCapture(event, `Media key (${event.key})`);
    }
  };

  // --- Gamepad / Bluetooth device buttons ---
  const handleRemoteControl = (event) => {
    if (
      event.type === "gamepadbuttonpress" ||
      event.type === "bluetoothdevicebuttonpress"
    ) {
      triggerCapture(event, event.type);
    }
  };

  // Add listeners to both document and window for better compatibility
  document.addEventListener("keydown", handleKeyDown, { passive: false, capture: true });
  document.addEventListener("keyup", handleKeyDown, { passive: false, capture: true });
  document.addEventListener("keydown", handleMediaKey, { passive: false, capture: true });
  document.addEventListener("gamepadbuttonpress", handleRemoteControl, { passive: false });
  document.addEventListener("bluetoothdevicebuttonpress", handleRemoteControl, { passive: false });
  
  // Also add to window for Android volume buttons
  window.addEventListener("keydown", handleKeyDown, { passive: false, capture: true });
  window.addEventListener("keyup", handleKeyDown, { passive: false, capture: true });

  // --- Optional Cordova-style (mobile volume buttons) ---
  const handleVolumeUp = (e) => triggerCapture(e, "Mobile VolumeUp");
  const handleVolumeDown = (e) => triggerCapture(e, "Mobile VolumeDown");

  window.addEventListener("volumeupbutton", handleVolumeUp, { passive: false });
  window.addEventListener("volumedownbutton", handleVolumeDown, { passive: false });

  return () => {
    document.removeEventListener("keydown", handleKeyDown, { capture: true });
    document.removeEventListener("keyup", handleKeyDown, { capture: true });
    document.removeEventListener("keydown", handleMediaKey, { capture: true });
    document.removeEventListener("gamepadbuttonpress", handleRemoteControl);
    document.removeEventListener("bluetoothdevicebuttonpress", handleRemoteControl);
    window.removeEventListener("keydown", handleKeyDown, { capture: true });
    window.removeEventListener("keyup", handleKeyDown, { capture: true });
    window.removeEventListener("volumeupbutton", handleVolumeUp);
    window.removeEventListener("volumedownbutton", handleVolumeDown);
  };
}, [openCamera, image, timerActive, capturePhoto]);

// Add a general event listener to log ALL key presses for debugging
useEffect(() => {
  const logAllKeys = (event) => {
    console.log('🎹 Key pressed - keyCode:', event.keyCode, 'key:', event.key, 'code:', event.code);
  };
  
  if (openCamera) {
    // Prevent body from scrolling when volume keys are pressed
    document.body.style.overflow = 'hidden';
    
    window.addEventListener('keydown', logAllKeys, { passive: false, capture: true });
    return () => {
      window.removeEventListener('keydown', logAllKeys, { capture: true });
      // Restore scrolling when camera closes
      document.body.style.overflow = '';
    };
  }
}, [openCamera]);



  // Timer functionality
  useEffect(() => {
    if (timerActive && timer > 0) {
      timerRef.current = setTimeout(() => {
        setTimer(timer - 1);
      }, 1000);
    } else if (timerActive && timer === 0) {
      setTimerActive(false);
      capturePhoto();
      toast.success("Timer finished! Photo captured.");
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [timer, timerActive, capturePhoto]);

  const enterGallery = () => {
    navigate(`/photo-capture?type=${bodyPart.value}`);
  };

  // Timer functions
  const startTimer = (seconds) => {
    setTimer(seconds);
    setTimerActive(true);
    setShowTimerOptions(false);
    toast.info(`Timer started: ${seconds} seconds`);
  };

  const cancelTimer = () => {
    setTimerActive(false);
    setTimer(0);
    toast.info("Timer cancelled");
  };

  // Adaptive video constraints based on device capabilities
  const getVideoConstraints = () => {
    const baseConstraints = {
      facingMode: isFrontCamera ? "user" : "environment",
      aspectRatio: { ideal: 16/9 },
      frameRate: { ideal: 30, max: 60 }
    };

    if (deviceCapabilities) {
      // Use device-specific optimal settings
      baseConstraints.width = { 
        ideal: deviceCapabilities.maxWidth, 
        min: Math.min(deviceCapabilities.maxWidth * 0.75, 720)
      };
      baseConstraints.height = { 
        ideal: deviceCapabilities.maxHeight, 
        min: Math.min(deviceCapabilities.maxHeight * 0.75, 480)
      };

      // Add advanced features for high-end devices
      if (deviceCapabilities.isHighResScreen && !deviceCapabilities.isMobile) {
        baseConstraints.advanced = [
          { whiteBalanceMode: 'auto' },
          { exposureMode: 'auto' },
          { focusMode: 'auto' },
          { torch: false }
        ];
      }
    } else {
      // Fallback to high-quality defaults
      baseConstraints.width = { ideal: 1920, min: 1280 };
      baseConstraints.height = { ideal: 1080, min: 720 };
    }

    return baseConstraints;
  };

  const videoConstraints = getVideoConstraints();

  const handleRetake = () => {
    setImage(null);
    setOpenCamera(true);
  };

  const toggleCamera = () => {
    setIsFrontCamera(!isFrontCamera);
  };

  // 🔹 Save image to backend
  const handleSubmit = async () => {
    if (isSaving) {
      toast.info("Already saving image, please wait...");
      return;
    }

    if (!image) {
      toast.error("Please capture an image first.");
      return;
    }

    try {
      if (!profile?.accessToken) {
        toast.error("You are not authenticated");
        return;
      }

      setIsSaving(true);

      const blob = await fetch(image).then((res) => res.blob());
      const formData = new FormData();
      formData.append("body_part", bodyPart.value);
      formData.append("image", blob, `${bodyPart.value}.jpg`);

      const res = await fetch(`${API_BASE}accounts/body-part-images/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${profile.accessToken}` },
        body: formData,
      });

      if (!res.ok) throw new Error("Upload failed");
      
      toast.success("Image saved successfully.");

      // reset for next photo
      setTimeout(() => {
        setImage(null);
        setOpenCamera(true);
        setIsSaving(false);
      }, 1500);
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to save image.");
      setIsSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-black">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full">
        <Toaster position="top-center" richColors />
        <Topbar pageTitle={`Photo Capture ${type ? "- " + bodyPart.value : ""}`} />

        {/* Instructions page */}
        {!type && (
          <>
            <div className="bg-[#1C1C1E] rounded-xl p-4 sm:p-6 md:p-8 w-full max-w-5xl mx-auto mt-6 md:mt-10">
              <h2 className="text-xl md:text-2xl font-bold mb-2 text-[#ceb46a]">
                Photo Capture Instructions
              </h2>
              <ul className="list-disc list-inside text-white font-medium">
                <li>
                  <em>Capture photos for the contest categories you plan on joining</em>
                </li>
                <li>
                  <em>
                    When capturing photos, it&apos;s your choice whether to include or exclude
                    your face and/or identifying marks
                  </em>
                </li>
                <li>
                  <em>Select manual or auto entry for contest participation (auto entry recommended)</em>
                </li>
              </ul>
            </div>

            <div className="bg-[#1C1C1E] rounded-xl p-4 sm:p-6 w-full max-w-3xl mx-auto mt-8 md:mt-16">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-center text-[#ceb46a]">
                Select Body Parts to Capture
              </h2>
              <form className="w-full mx-auto">
                <SelectList
                  label="Select Body Part"
                  options={bodyParts}
                  value={bodyPart}
                  onChange={selectBodyPart}
                />
              </form>
              <div className="flex justify-end space-x-4 mt-6">
                <button className="py-2 px-4 border-2 border-[#CEB46A] rounded-md text-[#CEB46A]">
                  Cancel
                </button>
                <button
                  onClick={enterGallery}
                  className="py-2 px-6 bg-[#CEB46A] text-black rounded-md"
                >
                  Enter Gallery
                </button>
              </div>
            </div>
          </>
        )}

        {/* Camera Page */}
        {type && (
          <div className="max-w-lg mx-auto p-4 min-h-[calc(100vh-100px)] flex flex-col">
            {/* Body part buttons */}
            <div className="flex items-center overflow-x-auto gap-3 pb-4">
              {bodyParts.map((part) => (
                <button
                  key={part.value}
                  onClick={() => selectBodyPart(part)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium ${
                    bodyPart.value === part.value
                      ? "bg-[#CEB46A] text-black"
                      : "bg-[#1C1C1E] text-white border border-[#333]"
                  }`}
                >
                  {part.label}
                </button>
              ))}
            </div>

            {/* Scrollable preview area */}
            <div className="overflow-y-auto max-h-[65vh] flex-1 space-y-6">
              <div className="border-2 border-dashed border-[#CEB46A] rounded-lg cursor-pointer flex flex-col items-center justify-center relative">
                {image ? (
                  <div className="h-[400px] w-full">
                    <img
                      src={image}
                      alt="Captured"
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                ) : openCamera ? (
                  <div 
                    className="h-[400px] w-full relative focus:outline-none"
                    tabIndex="0"
                    onFocus={() => console.log('📷 Camera container focused - volume buttons should work')}
                    ref={(el) => {
                      if (el && openCamera) {
                        // Auto-focus the container when camera opens
                        setTimeout(() => el.focus(), 100);
                      }
                    }}
                  >
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      screenshotQuality={1.0}
                      videoConstraints={videoConstraints}
                      className="w-full h-full object-cover rounded-lg"
                      // Additional props for better quality
                      mirrored={isFrontCamera}
                      // Force high quality mode
                      forceScreenshotSourceSize={true}
                      // Optimize for photo capture
                      imageSmoothing={true}
                    />
                    {/* Camera Toggle Button */}
                    <button
                      onClick={toggleCamera}
                      className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-200 backdrop-blur-sm"
                      title={`Switch to ${isFrontCamera ? "Rear" : "Front"} Camera`}
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </button>
                    
                    {/* Timer Display */}
                    {timerActive && (
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500/80 text-white px-6 py-3 rounded-full text-2xl font-bold backdrop-blur-sm">
                        {timer}
                      </div>
                    )}

                    {/* Timer Controls */}
                    {!timerActive && (
                      <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-2 rounded-lg backdrop-blur-sm">
                        <button
                          onClick={() => setShowTimerOptions(!showTimerOptions)}
                          className="text-sm font-medium hover:text-[#CEB46A] transition-colors"
                        >
                          Timer {showTimerOptions ? '▼' : '▶'}
                        </button>
                        {showTimerOptions && (
                          <div className="absolute bottom-full left-0 mb-2 bg-black/80 p-3 rounded-lg space-y-2 min-w-[120px]">
                            <div className="text-xs text-gray-300 mb-2">Set Timer:</div>
                            {[3, 5, 10].map((seconds) => (
                              <button
                                key={seconds}
                                onClick={() => startTimer(seconds)}
                                className="block w-full text-left text-sm hover:text-[#CEB46A] transition-colors"
                              >
                                {seconds}s
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Cancel Timer Button */}
                    {timerActive && (
                      <button
                        onClick={cancelTimer}
                        className="absolute bottom-4 left-4 bg-red-500/80 text-white px-3 py-2 rounded-lg text-sm font-medium backdrop-blur-sm hover:bg-red-600/80 transition-colors"
                      >
                        Cancel Timer
                      </button>
                    )}

                    {/* Capture hint and hardware button removed due to limited support */}

                  </div>
                ) : (
                  <div className="w-32 h-64 flex flex-col items-center justify-center text-blue-900 mb-2">
                    <p className="text-[#777] font-medium mt-5">Capture image</p>
                  </div>
                )}
              </div>
            </div>

            {/* Confirm Buttons */}
            <div className="sticky bottom-0 bg-black pt-4">
              <div className="flex gap-4">
                {openCamera && !image && !timerActive && (
                  <>
                    <button
                      onClick={() => setOpenCamera(false)}
                      className="w-1/2 bg-transparent border border-[#CEB46A] text-[#CEB46A] py-3 px-4 rounded-lg font-medium transition-colors hover:bg-[#ceb46a]/10"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={capturePhoto}
                      className="w-1/2 bg-[#CEB46A] text-black py-3 px-4 rounded-lg font-medium transition-colors hover:bg-[#d6c074]"
                    >
                      Capture
                    </button>
                  </>
                )}

                {openCamera && !image && timerActive && (
                  <div className="w-full text-center py-3 px-4 rounded-lg font-medium bg-gray-800 text-gray-400">
                    Timer Active - Photo will be captured automatically
                  </div>
                )}

                {image && (
                  <>
                    <button
                      onClick={handleRetake}
                      className="w-1/2 bg-transparent border border-[#CEB46A] text-[#CEB46A] py-3 px-4 rounded-lg font-medium transition-colors hover:bg-[#ceb46a]/10"
                    >
                      Retake
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={isSaving}
                      className={`w-1/2 py-3 px-4 rounded-lg font-medium transition-colors ${
                        isSaving 
                          ? "bg-gray-500 text-gray-300 cursor-not-allowed" 
                          : "bg-[#CEB46A] text-black hover:bg-[#d6c074]"
                      }`}
                    >
                      {isSaving ? "Saving..." : "Save"}
                    </button>
                  </>
                )}

                {!openCamera && !image && (
                  <button
                    onClick={() => setOpenCamera(true)}
                    className="w-full bg-[#CEB46A] text-black py-3 px-4 rounded-lg font-medium transition-colors hover:bg-[#d6c074]"
                  >
                    Open Camera
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
